import {autobind} from 'core-decorators'
import _ from 'lodash'
import * as React from 'react'
import {compose, pure, mapProps} from 'recompose'

import {cloneCreateOrCall, withTheme} from '@base/react-utils'
import withFocusedState from '@base/react-utils/withFocusedState'

import type {FeedbackStateType} from '@base-uikit/feedback'
import withFiltering, {
  withFilteringState,
} from '@base-uikit/lists/behaviours/withFiltering'
import withKeyboardNavigation, {
  withKeyboardNavigationState,
} from '@base-uikit/lists/behaviours/withKeyboardNavigation'
import DropdownBalloon from '@base-uikit/popovers/DropdownBalloon'
import withPopoverSelect, {
  withPopoverSelectState,
} from '@base-uikit/selects/SelectController/withPopoverSelect'
import withPopoverSelectUI from '@base-uikit/selects/SelectController/withPopoverSelectUI'
import withSelectController from '@base-uikit/selects/SelectController/withSelectController'

import {withClear} from '../behaviours/clearable'
import InlineTextInput from '../InlineTextInput'
import TextInput from '../TextInput'
import {parseTime, createTimeSlots} from './timeInputSelectUtils'

import baseTheme from './TimeInputSelect.css'

export type StepInterval = 15 | 30 | 60
export type TimeFormat = 'HH:mm' | 'hh:mm A' | 'hh:mm a'

export type TimeInputSelectProps = {
  value?: string | null
  step: StepInterval
  timeFormat: TimeFormat
  onValueChange?: (value?: string | null, fromDropdown?: boolean | null) => void
  feedback?: FeedbackStateType | null
  invalid?: boolean
  focused?: boolean
  disabled?: boolean
  clearable?: boolean
  children?: React.ReactNode
  onKeyDown?: Function
  canClear?: boolean
  inputComponent: any
}

export type TimeInputSelectState = {
  value: string | undefined | null
  inputValue: string | undefined | null
}

const createProvider = (
  inputValue: string,
  step: StepInterval,
  timeFormat: string
) => {
  const momentCmp = (x) => x.valueOf()

  const inputTime = parseTime(inputValue)
  let timeSlots = createTimeSlots(step, inputTime)
  let valueIndex = 0

  if (inputTime) {
    valueIndex = _.sortedIndexBy(timeSlots, inputTime, momentCmp)
    timeSlots.splice(valueIndex, 0, inputTime)
    timeSlots = _.sortedUniqBy(timeSlots, momentCmp)
  }

  return {
    items: timeSlots.map((slot) => slot.format(timeFormat)),
    focusedIndex: valueIndex,
  }
}

const timeInputDecorator = compose(
  pure,
  withTheme(baseTheme),
  mapProps((props) => ({
    // @ts-ignore HOCs in the composition lack types
    ...props,
    showFilteringInput: 'never',
    focusFiltered: true,
    // @ts-ignore Ignored when bumping to TS 4.4.3
    ...createProvider(props.value, props.step, props.timeFormat),
  })),
  withFilteringState(),
  withPopoverSelectState(),
  withKeyboardNavigationState(),
  withFocusedState(),

  withClear(),
  withFiltering(),
  withPopoverSelect({multiple: false}),
  withSelectController({
    multiple: false,
    preventDefaultEnterWhenFiltered: true,
    checkmark: 'tick',
  }),
  withKeyboardNavigation(),
  withPopoverSelectUI({
    popoverBalloon: DropdownBalloon,
    animation: 'dropdown',
    editable: true,
    toggleOnClick: true,
    focusBoundaryAt: false,
  })
)

const TimeDecoratedInput = (Component) =>
  timeInputDecorator((props) => {
    return <Component {...props} />
  })
const TimeSelect = TimeDecoratedInput(TextInput)

// eslint-disable-next-line react/no-unsafe
class AbstractTimeInputSelect extends React.Component<
  TimeInputSelectProps,
  TimeInputSelectState
> {
  state: TimeInputSelectState = {
    value: undefined,
    inputValue: undefined,
  }
  static defaultProps = {
    step: 30,
    timeFormat: 'hh:mm A',
    canClear: true,
    inputComponent: TimeSelect,
  }

  constructor(props: TimeInputSelectProps) {
    super(props)
    this.state = {
      value: this.formatTimeValue(props.value),
      inputValue: this.formatTimeValue(props.value),
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: TimeInputSelectProps) {
    const formattedTime = this.formatTimeValue(nextProps.value)
    const {value, inputValue} = this.state
    if (value === formattedTime && inputValue !== formattedTime) {
      this.handleTimeInputValueSelect()
    } else {
      this.setState({value: formattedTime, inputValue: formattedTime})
    }
  }

  @autobind
  formatTimeValue(timeValue?: string | null): string | undefined | null {
    const time = parseTime(timeValue)
    return time ? time.format(this.props.timeFormat) : null
  }

  @autobind
  handleTimeInputValueChange(value: string) {
    this.setState({inputValue: value})
    if (this.props.canClear && _.isEmpty(value)) {
      this.handleValueSelect(undefined)
    }
  }

  @autobind
  handleTimeInputValueSelect() {
    const {inputValue} = this.state
    this.handleValueSelect(inputValue)
  }

  @autobind
  handleValueSelect(value?: string | null) {
    this.setState({value, inputValue: value})
    if (this.props.onValueChange) {
      this.props.onValueChange(value)
    }
  }

  @autobind
  handleDropdownToggle(value?: string | null) {
    this.setState({value, inputValue: value})
    if (this.props.onValueChange) {
      this.props.onValueChange(value, true)
    }
  }

  render() {
    const {inputComponent} = this.props
    const {inputValue} = this.state

    return cloneCreateOrCall(inputComponent, {
      ...this.props,
      onItemToggle: this.handleDropdownToggle,
      onValueChange: this.handleTimeInputValueChange,
      onSubmit: this.handleTimeInputValueSelect,
      onBlur: this.handleTimeInputValueSelect,
      value: inputValue,
      theme: baseTheme,
    })
  }
}

export const TimeInputSelect = (props: TimeInputSelectProps) => (
  <AbstractTimeInputSelect {...props} inputComponent={TimeSelect} />
)

const InlineTimeSelect = TimeDecoratedInput(InlineTextInput)
export const InlineTimeInputSelect = (props: TimeInputSelectProps) => (
  <AbstractTimeInputSelect {...props} inputComponent={InlineTimeSelect} />
)
